import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Badge,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Textarea,
  Input,
  Label,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@tonic/shared/components/ui';
import {
  ArrowLeft,
  Clock,
  User,
  Wrench,
  ClipboardCheck,
  Package,
  Calendar,
  MessageSquare,
  History,
  Camera,
  FileText,
  MoreVertical,
  Send,
  Phone,
  Mail,
} from 'lucide-react';

const mockRepairDetail = {
  id: "REP00545897",
  status: "In Progress",
  priority: "High",
  customer: {
    id: "CUST123",
    name: "John Smith",
    email: "john.smith@email.com",
    phone: "+1 (555) 123-4567",
    address: "123 Main St, Anytown, ST 12345",
    membershipLevel: "IKEA Family Plus",
    previousRepairs: 3
  },
  product: {
    id: "BILLY-80263832",
    name: "BILLY Bookcase",
    category: "Storage",
    purchaseDate: "2023-09-15",
    warranty: "Valid until 2025-09-15",
    serialNumber: "BIL2023091500123",
    color: "White",
    dimensions: "80x28x202 cm"
  },
  repair: {
    program: "IKEA Furniture Care",
    type: "Structural Repair",
    issue: "Damaged hinges and loose shelves",
    description: "Customer reported wobbling shelves and misaligned doors. Initial assessment shows worn out hinges and loose shelf supports.",
    requestDate: "2024-03-15T10:43:00Z",
    estimatedCompletion: "2024-03-20",
    assignedTechnician: {
      id: "TECH001",
      name: "Mike Wilson",
      specialization: "Furniture Assembly",
      rating: 4.8,
      completedRepairs: 128
    },
    location: "Main Workshop - Bay 3",
    cost: {
      parts: 15.99,
      labor: 45.00,
      total: 60.99
    }
  },
  notes: [
    {
      id: "NOTE001",
      author: "Mike Wilson",
      timestamp: "2024-03-15T11:00:00Z",
      content: "Initial assessment completed. Found damaged hinges and loose shelf supports. Parts ordered.",
      type: "assessment"
    },
    {
      id: "NOTE002",
      author: "Sarah Brown",
      timestamp: "2024-03-15T14:30:00Z",
      content: "Parts received. Repair scheduled for tomorrow morning.",
      type: "logistics"
    }
  ],
  photos: [
    {
      id: "PHOTO001",
      url: "/photos/repair/001.jpg",
      timestamp: "2024-03-15T11:00:00Z",
      description: "Damaged hinge - right door"
    },
    {
      id: "PHOTO002",
      url: "/photos/repair/002.jpg",
      timestamp: "2024-03-15T11:02:00Z",
      description: "Loose shelf support - upper left"
    }
  ],
  documents: [
    {
      id: "DOC001",
      name: "Original Purchase Receipt",
      type: "receipt",
      uploadedAt: "2024-03-15T10:45:00Z"
    },
    {
      id: "DOC002",
      name: "Warranty Card",
      type: "warranty",
      uploadedAt: "2024-03-15T10:45:00Z"
    }
  ],
  progressSteps: [
    { label: "Received", completed: true, timestamp: "2024-03-15T10:43:00Z" },
    { label: "Assessment", completed: true, timestamp: "2024-03-15T11:00:00Z" },
    { label: "Parts Ordered", completed: true, timestamp: "2024-03-15T11:30:00Z" },
    { label: "In Repair", completed: true, timestamp: "2024-03-15T14:30:00Z" },
    { label: "Quality Check", completed: false },
    { label: "Ready", completed: false }
  ],
  timeline: [
    {
      id: "EVENT001",
      type: "status_change",
      description: "Repair request received",
      timestamp: "2024-03-15T10:43:00Z",
      actor: "System"
    },
    {
      id: "EVENT002",
      type: "assessment",
      description: "Initial assessment completed",
      timestamp: "2024-03-15T11:00:00Z",
      actor: "Mike Wilson"
    },
    {
      id: "EVENT003",
      type: "parts",
      description: "Replacement parts ordered",
      timestamp: "2024-03-15T11:30:00Z",
      actor: "Sarah Brown"
    }
  ]
};

function getStatusColor(status) {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'default';
    case 'in progress':
      return 'secondary';
    case 'pending':
      return 'outline';
    case 'cancelled':
      return 'destructive';
    default:
      return 'outline';
  }
}

function getPriorityColor(priority) {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'secondary';
    case 'low':
      return 'outline';
    default:
      return 'outline';
  }
}

export function RepairDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState('details');
  const [newNote, setNewNote] = React.useState('');

  // In a real app, we would fetch the repair details using the id
  const repair = mockRepairDetail;

  const handleStatusChange = (newStatus) => {
    console.log("Status changed to:", newStatus);
    // Update status logic here
  };

  const handleNotesUpdate = (newNotes) => {
    console.log("Notes updated:", newNotes);
    // Update notes logic here
  };

  const handleBackClick = () => {
    navigate('/admin/re-commerce/repairs/list');
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      console.log("Adding note:", newNote);
      setNewNote('');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBackClick}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Repair #{repair.id}</h1>
            <p className="text-sm text-muted-foreground">{repair.product.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={getPriorityColor(repair.priority)}>
            {repair.priority}
          </Badge>
          <Badge variant={getStatusColor(repair.status)}>
            {repair.status}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Update Status</DropdownMenuItem>
              <DropdownMenuItem>Reassign Technician</DropdownMenuItem>
              <DropdownMenuItem>Print Details</DropdownMenuItem>
              <DropdownMenuItem>Export Report</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="progress">Progress</TabsTrigger>
          <TabsTrigger value="notes">Notes & Photos</TabsTrigger>
          <TabsTrigger value="customer">Customer</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Repair Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Program</Label>
                    <p className="font-medium">{repair.repair.program}</p>
                  </div>
                  <div>
                    <Label>Type</Label>
                    <p className="font-medium">{repair.repair.type}</p>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <p className="font-medium">{repair.repair.location}</p>
                  </div>
                  <div>
                    <Label>Request Date</Label>
                    <p className="font-medium">{new Date(repair.repair.requestDate).toLocaleDateString()}</p>
                  </div>
                </div>
                <div>
                  <Label>Issue Description</Label>
                  <p className="mt-1">{repair.repair.description}</p>
                </div>
                <div>
                  <Label>Cost Breakdown</Label>
                  <div className="mt-1 space-y-1">
                    <div className="flex justify-between">
                      <span>Parts:</span>
                      <span>${repair.repair.cost.parts}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Labor:</span>
                      <span>${repair.repair.cost.labor}</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Total:</span>
                      <span>${repair.repair.cost.total}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Product Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Product ID</Label>
                    <p className="font-medium">{repair.product.id}</p>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <p className="font-medium">{repair.product.category}</p>
                  </div>
                  <div>
                    <Label>Purchase Date</Label>
                    <p className="font-medium">{repair.product.purchaseDate}</p>
                  </div>
                  <div>
                    <Label>Warranty</Label>
                    <p className="font-medium">{repair.product.warranty}</p>
                  </div>
                  <div>
                    <Label>Serial Number</Label>
                    <p className="font-medium">{repair.product.serialNumber}</p>
                  </div>
                  <div>
                    <Label>Dimensions</Label>
                    <p className="font-medium">{repair.product.dimensions}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Assigned Technician</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium">{repair.repair.assignedTechnician.name}</p>
                  <p className="text-sm text-muted-foreground">{repair.repair.assignedTechnician.specialization}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-500">★</span>
                    <span className="font-medium">{repair.repair.assignedTechnician.rating}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {repair.repair.assignedTechnician.completedRepairs} repairs completed
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="progress" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Repair Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-4">
                  {repair.progressSteps.map((step, index) => (
                    <div key={step.label} className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full ${step.completed ? 'bg-green-500' : 'bg-gray-200'
                        }`} />
                      <div className="flex-1">
                        <span className={step.completed ? 'text-gray-900' : 'text-gray-500'}>
                          {step.label}
                        </span>
                        {step.timestamp && (
                          <span className="text-sm text-muted-foreground ml-2">
                            {new Date(step.timestamp).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {repair.timeline.map((event) => (
                  <div key={event.id} className="flex items-start gap-4">
                    <div className="w-2 h-2 mt-2 rounded-full bg-blue-500" />
                    <div className="flex-1">
                      <p className="font-medium">{event.description}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{event.actor}</span>
                        <span>•</span>
                        <span>{new Date(event.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
                <Button onClick={handleAddNote}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4">
                {repair.notes.map((note) => (
                  <div key={note.id} className="flex items-start gap-4">
                    <div className="w-2 h-2 mt-2 rounded-full bg-blue-500" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{note.author}</span>
                        <span className="text-sm text-muted-foreground">
                          {new Date(note.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-1">{note.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Photos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {repair.photos.map((photo) => (
                  <div key={photo.id} className="space-y-2">
                    <div className="aspect-video bg-neutral-100 rounded-lg flex items-center justify-center">
                      <Camera className="w-8 h-8 text-neutral-400" />
                    </div>
                    <div>
                      <p className="font-medium">{photo.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(photo.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customer" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <p className="font-medium">{repair.customer.name}</p>
                </div>
                <div>
                  <Label>Membership</Label>
                  <p className="font-medium">{repair.customer.membershipLevel}</p>
                </div>
                <div>
                  <Label>Email</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{repair.customer.email}</p>
                    <Button variant="outline" size="sm">
                      <Mail className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Phone</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{repair.customer.phone}</p>
                    <Button variant="outline" size="sm">
                      <Phone className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <p className="font-medium">{repair.customer.address}</p>
                </div>
              </div>
              <div>
                <Label>Previous Repairs</Label>
                <p className="font-medium">{repair.customer.previousRepairs} repairs</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {repair.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(doc.uploadedAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      View
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
